import { useState, useCallback, useEffect, useRef } from "react"
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material"

/**
 * Describes a single action available on a DataGrid row.
 *
 * @typedef {Object} RowAction
 * @property {string}               key       - Unique id for the action.
 * @property {string}               label     - Text shown in the tooltip and context-menu item.
 * @property {import('react').ReactNode} icon  - Icon element rendered inside the button / menu item.
 * @property {string}               [color]   - Base color (hex/rgb) for button tint. Defaults to #5b5b5f.
 * @property {boolean}              [show]    - When false the action is hidden entirely. Defaults to true.
 * @property {boolean}              [disabled]- When true the action is rendered but not interactive.
 * @property {() => void}           onSelect  - Handler called when the user activates the action.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages the state of a DataGrid row context-menu triggered by right-click.
 *
 * Usage: attach the handler with **onContextMenuCapture** (not onContextMenu) on the Box/Paper
 * that wraps DataGrid so clicks inside Tooltips, Chips, etc. still reach the row.
 *
 *   const { contextMenu, handleContextMenu, handleClose } = useDataGridRowContextMenu({...})
 *   <Paper onContextMenuCapture={handleContextMenu} sx={{ position: 'relative' }}>
 *     <DataGridRowContextMenu contextMenu={contextMenu} onClose={handleClose} />
 *     <DataGrid ... />
 *   </Paper>
 *
 * @param {{ getRowActions: (row: any) => RowAction[], rows: any[], getRowId?: (row: any) => any }} opts
 */
export function useDataGridRowContextMenu({ getRowActions, rows, getRowId }) {
  const [contextMenu, setContextMenu] = useState(null)

  const handleContextMenu = useCallback(
    (event) => {
      const t = event.target
      if (!t || typeof t.closest !== "function") return

      const rowEl = t.closest(".MuiDataGrid-row")
      if (!rowEl) return

      if (!rowEl.classList.contains("MuiDataGrid-row")) return

      event.preventDefault()

      const rawId = rowEl.getAttribute("data-id")
      if (rawId == null || rawId === "") return

      const resolveId = getRowId ?? ((r) => r.id)
      const row = rows.find((r) => String(resolveId(r)) === String(rawId))
      if (!row) return

      const actions = getRowActions(row).filter((a) => a.show !== false)
      if (actions.length === 0) return

      setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, row, actions })
    },
    [getRowActions, rows, getRowId]
  )

  const handleClose = useCallback(() => setContextMenu(null), [])

  return { contextMenu, handleContextMenu, handleClose }
}

// ─────────────────────────────────────────────────────────────────────────────
// Context-menu component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a MUI Menu anchored to the cursor position when contextMenu is not null.
 * - Backdrop uses pointer-events: none so a second right-click reaches the grid under the menu.
 * - Left-click outside the menu paper closes it (capture listener).
 *
 * @param {{ contextMenu: { mouseX: number, mouseY: number, actions: RowAction[] } | null, onClose: () => void }} props
 */
export function DataGridRowContextMenu({ contextMenu, onClose }) {
  const paperRef = useRef(null)

  useEffect(() => {
    if (!contextMenu) return

    const closeOnPointerDownOutside = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return
      const paper = paperRef.current
      if (paper && paper.contains(e.target)) return
      onClose()
    }

    document.addEventListener("pointerdown", closeOnPointerDownOutside, true)
    return () => document.removeEventListener("pointerdown", closeOnPointerDownOutside, true)
  }, [contextMenu, onClose])

  if (!contextMenu) return null

  const { mouseX, mouseY, actions } = contextMenu

  return (
    <Menu
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ top: mouseY, left: mouseX }}
      slotProps={{
        backdrop: {
          sx: { pointerEvents: "none" },
        },
        paper: {
          ref: paperRef,
          elevation: 4,
          sx: {
            minWidth: 200,
            borderRadius: 2,
            py: 0.5,
            pointerEvents: "auto",
            "& .MuiMenuItem-root": { borderRadius: 1, mx: 0.5, px: 1.5 },
          },
        },
      }}
      sx={{ zIndex: 1400 }}
    >
      {actions.map((action, idx) => {
        const isDivider = action.key === "__divider__"
        if (isDivider) return <Divider key={`divider-${idx}`} sx={{ my: 0.5 }} />

        return (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              onClose()
              action.onSelect()
            }}
            dense
          >
            {action.icon && (
              <ListItemIcon sx={{ minWidth: 32, color: action.color || "#5b5b5f" }}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={action.label}
              slotProps={{ primary: { variant: "body2" } }}
            />
          </MenuItem>
        )
      })}
    </Menu>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Action cell component (reuses the same RowAction[] for the column)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drop-in replacement for the JSX inside renderCell of an "Acciones" column.
 * Renders an IconButton + Tooltip per visible action, styled consistently.
 *
 * @param {{ actions: RowAction[], size?: 'small' | 'medium' }} props
 */
export function DataGridActionCell({ actions, size = "small" }) {
  const visible = actions.filter((a) => a.show !== false)
  if (visible.length === 0) return null

  return (
    <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }}>
      {visible.map((action) => {
        const color = action.color || "#5b5b5f"
        return (
          <Tooltip key={action.key} title={action.label}>
            {/* span wrapper allows Tooltip to work when button is disabled */}
            <span>
              <IconButton
                className="colorGeneralParaIconos"
                size={size}
                disabled={action.disabled}
                onClick={action.onSelect}
                sx={{
                  bgcolor: alpha(color, 0.1),
                  "&:hover": { bgcolor: alpha(color, 0.2) },
                  padding: size === "small" ? "4px" : "8px",
                }}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        )
      })}
    </Box>
  )
}
